import { Component, Inject, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import * as moment from 'moment'
import { CrudOps } from '../../../../api/constants/crud.enum'
import { Employee, EmployeePaginatedList } from '../../../../api/models/employee.model'
import { TimeCard, TimeCardStatus } from '../../../../api/models/timecard.model'
import { TimesheetProject, TimesheetProjectPaginatedList } from '../../../../api/models/timesheet-project.model'
import { TimeCardsService } from '../../../../api/services/timecards.service'
import { LoaderService } from '../../../common/services/loader.service'
import { SnackbarService } from '../../../common/services/snackbar.service'
import { notUndefined } from '../../../common/utils/conditional-assert'
import { Utils } from '../../../common/utils/utils'
import {
    atleastOneChargeRequired,
    chargeFormatValidator,
    legitimateEmployeeValidator,
    legitimateTimesheetProjectValidator,
    maxTwelveHourCharge
} from '../../../common/validators/validators'
import { TimeCardContainer } from '../model/timecard-container.model'

export interface TimecardDialogData {
    crud: CrudOps
    selectedTimeCardContainerGroup: Array<TimeCardContainer> | undefined
    employeeSubjectList: EmployeePaginatedList
    timeSheetProjectSubjectList: TimesheetProjectPaginatedList
}

@Component({
    selector: 'ppw-app-timecard-dialog',
    templateUrl: './timecard-dialog.component.html',
    styleUrls: ['./timecard-dialog.component.scss']
})
export class TimecardDialogComponent implements OnInit {
    trackBy = Utils.trackByIndex
    isBusy? = false
    dialogTitle!: string
    crud!: CrudOps
    timecardFormGroup!: UntypedFormGroup
    timeCard?: TimeCardContainer
    selectedDates!: Array<moment.Moment>
    employees!: Array<Employee>
    filteredEmployees!: Array<Employee>
    timeSheetProjects!: Array<TimesheetProject>
    filteredTimeSheetProjects!: Array<TimesheetProject>
    CHARGE_INFO =
        'Units:\n' +
        "'d' = days (8 working hours)\n" +
        "'h' = hours\n" +
        "'m' = minutes\n" +
        '\n' +
        'Examples:\n' +
        '* 1d 2h 15m\n' +
        '* 1d2h15m\n' +
        '* 0.5d or 0,5d\n'
    startAt!: moment.Moment
    secondsToInvoiceTotal: number
    secondsNoInvoiceTotal: number

    employeeControl!: UntypedFormControl
    clientProjectControl!: UntypedFormControl
    issueTitleControl!: UntypedFormControl
    isMultiSelectedTimecards!: boolean
    toChargeControl!: UntypedFormControl
    noChargeControl!: UntypedFormControl
    selectedTimeCardContainerGroup?: Array<TimeCardContainer>
    issuePriority?: string

    private selectedEmployee?: Employee
    private selectedProject?: TimesheetProject

    private readonly prefix = 'TimecardDialogComponent'

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: TimecardDialogData,
        private readonly matDialogRef: MatDialogRef<TimecardDialogComponent>,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly timeCardsService: TimeCardsService,
        private readonly snackbarService: SnackbarService,
        private readonly loaderService: LoaderService
    ) {
        this.secondsNoInvoiceTotal = 0
        this.secondsToInvoiceTotal = 0
    }

    ngOnInit(): void {
        this.crud = this.data.crud
        this.employees = this.data.employeeSubjectList.items.sort(Employee.fullNameCompareFn)
        this.timeSheetProjects = this.data.timeSheetProjectSubjectList.items.filter(
            (project) => !project.name.startsWith('[ARCHIVED')
        )
        this.filteredEmployees = this.employees
        this.filteredTimeSheetProjects = this.timeSheetProjects
        this.selectedTimeCardContainerGroup = this.data.selectedTimeCardContainerGroup
            ? this.data.selectedTimeCardContainerGroup.map((data) => ({
                  ...data,
                  employeeFullName: data.employeeFullName
              }))
            : undefined
        this.issuePriority = this.getIssuePriority()
        this.setTimecard()
        this.setSelectedDates()
        this.crudHandler()
        this.buildTimecardForm(this.selectedTimeCardContainerGroup)
    }

    setTimecard(): void {
        if (this.selectedTimeCardContainerGroup) {
            this.selectedTimeCardContainerGroup.length >= 2
                ? (this.isMultiSelectedTimecards = true)
                : (this.isMultiSelectedTimecards = false)
            if (this.selectedTimeCardContainerGroup.length === 1) {
                this.timeCard = this.selectedTimeCardContainerGroup[0]
                this.secondsToInvoiceTotal = this.timeCard.secondsToInvoice
                this.secondsNoInvoiceTotal = this.timeCard.secondsNoInvoice
                this.timeCard.hoursToCharge = Utils.secondsAsHours(this.timeCard.secondsToInvoice)
                this.timeCard.hoursNoCharge = Utils.secondsAsHours(this.timeCard.secondsNoInvoice)
                if (this.crud === CrudOps.Create) {
                    this.timeCard.id = undefined
                }
            }
            if (this.selectedTimeCardContainerGroup.length >= 2) {
                this.selectedTimeCardContainerGroup.forEach((selectedTimecard: TimeCardContainer) => {
                    this.secondsToInvoiceTotal += selectedTimecard.secondsToInvoice
                    this.secondsNoInvoiceTotal += selectedTimecard.secondsNoInvoice
                    selectedTimecard.hoursToCharge = Utils.secondsAsHours(selectedTimecard.secondsToInvoice)
                    selectedTimecard.hoursNoCharge = Utils.secondsAsHours(selectedTimecard.secondsNoInvoice)
                })
            }
        }
        if (!this.selectedTimeCardContainerGroup) {
            this.timeCard = new TimeCardContainer()
            const momentToday = moment().startOf('day')
            this.timeCard.date = momentToday.toDate()
            this.timeCard.secondsNoInvoice = 0
            this.timeCard.secondsToInvoice = 0
            this.timeCard.timecardStatusEnum = TimeCardStatus.EDITED
        }
    }

    setSelectedDates(): void {
        this.selectedDates = []
        if (this.selectedTimeCardContainerGroup && this.selectedTimeCardContainerGroup.length > 0) {
            this.selectedTimeCardContainerGroup.forEach((selectedTimecardContainer) => {
                this.selectedDates.push(moment(selectedTimecardContainer.date))
            })
        } else if (this.timeCard) {
            this.selectedDates.push(moment(this.timeCard.date))
        }
        this.startAt = moment()
        if (this.selectedDates.length > 0) {
            this.startAt = this.selectedDates[this.selectedDates.length - 1]
        }
    }

    get multipleMonthsSelected(): boolean {
        const monthsSet: Set<number> = new Set<number>()
        if (this.selectedDates.length > 1) {
            this.selectedDates.forEach((item) => {
                const selectedDate = item.toDate().getMonth()
                monthsSet.add(selectedDate)
            })
        }

        return monthsSet.size > 1
    }

    dateClass(): (date: moment.Moment) => MatCalendarCellCssClasses {
        return (date: moment.Moment): MatCalendarCellCssClasses => {
            const highlightDate = this.selectedDates.some(
                (d) => d.date() === date.date() && d.month() === date.month() && d.year() === date.year()
            )
            const isWeekend = date.weekday() === 0 || date.weekday() === 6

            return highlightDate ? 'selected' : isWeekend ? 'weekend' : ''
        }
    }

    disableCalendarOnReadOrDelete = () => !this.isReadDeleteOrLocked()

    crudHandler(): void {
        switch (this.data.crud) {
            case CrudOps.Create:
                this.dialogTitle = 'New Timecard'
                break
            case CrudOps.Update:
                this.isMultiSelectedTimecards
                    ? (this.dialogTitle = 'Edit Timecard Group')
                    : (this.dialogTitle = 'Edit Timecard')
                break
            case CrudOps.Delete:
                this.isMultiSelectedTimecards
                    ? (this.dialogTitle = 'Delete Timecard Group')
                    : (this.dialogTitle = 'Delete Timecard')
                break
            default:
                this.dialogTitle = ''
        }
    }

    employeeQuerySearch(input: string): void {
        this.filteredEmployees = this.employees
        this.filteredEmployees = this.employees.filter((employee: Employee) => {
            if (employee.fullName && employee.fullName !== '') {
                return employee.fullName.toLowerCase().includes(input.toLowerCase())
            }

            return false
        })
    }

    clearEmployee(): void {
        this.timecardFormGroup.controls.employee.setValue('')
    }

    isEmployeeSelected(): boolean {
        return this.timecardFormGroup.controls.employee.value !== ''
    }

    clearClient(): void {
        this.timecardFormGroup.controls.clientProject.setValue('')
    }

    isClientSelected(): boolean {
        return this.timecardFormGroup.controls.clientProject.value !== ''
    }

    isIssuePriorityRed(): boolean {
        return this.issuePriority === 'CRITICAL' || this.issuePriority === 'BLOCKER' || this.issuePriority === 'MAJOR'
    }

    clientQuerySearch($event: string): void {
        const input = $event
        this.filteredTimeSheetProjects = this.timeSheetProjects
        this.filteredTimeSheetProjects = this.timeSheetProjects.filter(
            (project: TimesheetProject) =>
                project.name.toLowerCase().includes(input.toLowerCase()) ||
                project.clientName.toLowerCase().includes(input.toLowerCase()) ||
                input === this.getProjectName(project)
        )
    }

    buildTimecardForm(timecardContainer?: Array<TimeCardContainer>): void {
        const isMultiEdit: boolean = (timecardContainer && timecardContainer.length > 1) ?? false
        let employee: Employee | null | undefined = timecardContainer
            ? this.employees.find((e) => e.id === timecardContainer[0].employeeId)
            : null
        let clientProjectName = timecardContainer ? this.getClientProjectName(timecardContainer[0]) : ''
        const employeeValidators = [
            Validators.required,
            legitimateEmployeeValidator(this.employees, this.setSelectedEmployee.bind(this), isMultiEdit)
        ]
        const clientProjectValidators = [
            Validators.required,
            legitimateTimesheetProjectValidator(
                this.timeSheetProjects,
                this.getProjectName.bind(this),
                this.setSelectedProject.bind(this),
                isMultiEdit
            )
        ]

        if (isMultiEdit) {
            employee = null
            clientProjectName = ''

            // Deletes the required validators.
            delete employeeValidators[0]
            delete clientProjectValidators[0]
        }

        this.employeeControl = new UntypedFormControl(
            {
                value: employee ? employee.fullName : '',
                disabled: this.isReadDeleteOrLocked()
            },
            employeeValidators
        )
        this.clientProjectControl = new UntypedFormControl(
            {
                value: clientProjectName,
                disabled: this.isReadDeleteOrLocked()
            },
            clientProjectValidators
        )
        this.issueTitleControl = new UntypedFormControl(
            { value: timecardContainer ? timecardContainer[0].issueTitle : '', disabled: this.isReadDeleteOrLocked() },
            [Validators.required]
        )
        this.toChargeControl = new UntypedFormControl(
            {
                value: timecardContainer ? Utils.secondsToTimeString(this.secondsToInvoiceTotal) : '',
                disabled: this.isReadDeleteOrLocked() || this.isMultiSelectedTimecards
            },
            [chargeFormatValidator()]
        )
        this.noChargeControl = new UntypedFormControl(
            {
                value: timecardContainer ? Utils.secondsToTimeString(this.secondsNoInvoiceTotal) : '',
                disabled: this.isReadDeleteOrLocked() || this.isMultiSelectedTimecards
            },
            [chargeFormatValidator()]
        )
        const crossFieldValidators = [atleastOneChargeRequired]
        if (!this.isMultiSelectedTimecards) {
            crossFieldValidators.push(maxTwelveHourCharge)
        }
        this.timecardFormGroup = this.formBuilder.group(
            {
                employee: this.employeeControl,
                clientProject: this.clientProjectControl,
                toCharge: this.toChargeControl,
                noCharge: this.noChargeControl,
                issueTitle: this.issueTitleControl,
                comment: [
                    {
                        value: timecardContainer ? timecardContainer[0].worklogComment : '',
                        disabled: this.isReadOrDelete()
                    }
                ]
            },
            { validators: crossFieldValidators }
        )
    }

    updateFormDate(date: moment.Moment, calendar: MatCalendar<any>): void {
        const selectedDateIndex = this.selectedDates.findIndex((selectedDate: moment.Moment) =>
            selectedDate.isSame(date, 'day')
        )
        if (selectedDateIndex >= 0) {
            this.selectedDates.splice(selectedDateIndex, 1)
        } else {
            this.selectedDates.push(date)
        }
        calendar.updateTodaysDate()
    }

    cancelDialog(): void {
        this.matDialogRef.close()
    }

    swapCharges(): void {
        const noChargeValue = this.noChargeControl.value
        const toChargeValue = this.toChargeControl.value
        this.toChargeControl.setValue(noChargeValue)
        this.noChargeControl.setValue(toChargeValue)
    }

    chargesToToCharge(): void {
        if (this.noChargeControl.value !== undefined) {
            const noChargeValueSec = Utils.timeUnitStringToSeconds(this.noChargeControl.value)
            const toChargeValueSec = Utils.timeUnitStringToSeconds(this.toChargeControl.value)
            const toChargeTotalTimeString = Utils.secondsToTimeString(noChargeValueSec + toChargeValueSec)
            this.toChargeControl.setValue(toChargeTotalTimeString)
            this.noChargeControl.setValue(undefined)
        }
    }

    chargesToNoCharge(): void {
        if (this.toChargeControl.value !== undefined) {
            const noChargeValueSec = Utils.timeUnitStringToSeconds(this.noChargeControl.value)
            const toChargeValueSec = Utils.timeUnitStringToSeconds(this.toChargeControl.value)
            const noChargeTotalTimeString = Utils.secondsToTimeString(noChargeValueSec + toChargeValueSec)
            this.noChargeControl.setValue(noChargeTotalTimeString)
            this.toChargeControl.setValue(undefined)
        }
    }

    async deleteTimecard(): Promise<void> {
        this.isBusy = true
        this.loaderService.startLoading()
        try {
            if (this.isMultiSelectedTimecards) {
                await this.deleteMultiTimecards()
            } else if (this.timeCard && this.timeCard.id) {
                await this.timeCardsService.deleteTimeCard(this.timeCard.id)
            }
            this.matDialogRef.close({ crud: this.data.crud })
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err, true)
        } finally {
            this.loaderService.stopLoading()
            this.isBusy = false
        }
    }

    async deleteMultiTimecards(): Promise<Array<boolean>> {
        const promises: Array<Promise<boolean>> = new Array<Promise<boolean>>()
        notUndefined(this.selectedTimeCardContainerGroup).forEach((selectedTimecard: TimeCardContainer) => {
            if (selectedTimecard && selectedTimecard.id) {
                promises.push(this.timeCardsService.deleteTimeCard(selectedTimecard.id))
            }
        })

        return Promise.all(promises)
    }

    async saveTimecard(): Promise<void> {
        this.isBusy = true
        this.loaderService.startLoading()
        try {
            if (this.crud === CrudOps.Create) {
                const savedTimecards = await this.createMultiTimecards()
                this.matDialogRef.close({ crud: this.data.crud, timecards: savedTimecards })
            } else if (this.crud === CrudOps.Update) {
                if (this.isMultiSelectedTimecards) {
                    // Updating multiple time cards at the same time.
                    const updatedTimecards = await this.updateMultiTimecard()
                    this.matDialogRef.close({ crud: this.data.crud, timecards: updatedTimecards })
                } else {
                    // Updating time cards starting from a single time card.
                    const savedTimecards = await this.updateSingleTimecard(this.timeCard)
                    this.matDialogRef.close({ crud: this.data.crud, timecards: savedTimecards })
                }
            }
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err, true)
        } finally {
            this.loaderService.stopLoading()
            this.isBusy = false
        }
    }

    async updateSingleTimecard(timecard: TimeCardContainer | undefined): Promise<Array<TimeCard>> {
        const promises: Array<Promise<TimeCard>> = new Array<Promise<TimeCard>>()

        // This will generate a TimeCard instance for each selected date.
        // Because we started from a single card instance, the first one is treated as the UPDATE and other
        // selected dates will become newly created time cards.
        const timecardsToSave: Array<TimeCard> = this.createOrEditTimecard()
        if (timecardsToSave.length > 0 && timecard) {
            timecardsToSave[0].id = timecard.id
        }

        timecardsToSave.forEach((tc: TimeCard) => {
            promises.push(this.timeCardsService.saveTimeCard(tc))
        })

        return Promise.all(promises)
    }

    async updateMultiTimecard(): Promise<Array<TimeCard>> {
        const promises: Array<Promise<TimeCard>> = new Array<Promise<TimeCard>>()
        const updatedTimecards: Array<TimeCard> = this.getUpdatedTimecards(
            notUndefined(this.selectedTimeCardContainerGroup)
        )
        updatedTimecards.forEach((timecard: TimeCard) => {
            promises.push(this.timeCardsService.saveTimeCard(timecard))
        })

        return Promise.all(promises)
    }

    async createMultiTimecards(): Promise<Array<TimeCard>> {
        const promises: Array<Promise<TimeCard>> = new Array<Promise<TimeCard>>()
        const createdTimecards: Array<TimeCard> = this.createOrEditTimecard()
        createdTimecards.forEach((timecard: TimeCard) => {
            promises.push(this.timeCardsService.saveTimeCard(timecard))
        })

        return Promise.all(promises)
    }

    getUpdatedTimecards(timecardContainer: Array<TimeCardContainer>): Array<TimeCard> {
        const updatedTimecards: Array<TimeCard> = new Array<TimeCard>()
        timecardContainer.forEach((timecard: TimeCardContainer) => {
            if (
                (this.selectedEmployee && this.selectedEmployee.id) ||
                (this.selectedProject && this.selectedProject.id)
            ) {
                timecard.date = timecard.date =
                    this.selectedDates.length === 1
                        ? this.selectedDates[0].startOf('day').utc(true).toDate()
                        : timecard.date

                if (this.selectedEmployee && this.selectedEmployee.id && this.employeeControl.dirty) {
                    timecard.employeeId = this.selectedEmployee.id
                }

                if (this.selectedProject && this.selectedProject.id && this.clientProjectControl.dirty) {
                    timecard.timesheetProjectName = this.selectedProject.name
                    timecard.timesheetProjectId = this.selectedProject.id
                    timecard.clientName = this.selectedProject.clientName
                }
            }
            if (!this.noChargeControl.value) {
                if (timecard.secondsNoInvoice) {
                    timecard.secondsToInvoice = timecard.secondsNoInvoice
                    timecard.secondsNoInvoice = 0
                }
            } else if (!this.toChargeControl.value) {
                if (timecard.secondsToInvoice) {
                    timecard.secondsNoInvoice = timecard.secondsToInvoice
                    timecard.secondsToInvoice = 0
                }
            }
            timecard.parent = undefined // avoid circular dependencies in json (we don't need to give this over the wire anyways)
            timecard.children = undefined
            updatedTimecards.push(timecard)
        })

        return updatedTimecards
    }

    createOrEditTimecard(): Array<TimeCard> {
        const result: Array<TimeCard> = []
        const formValue = this.timecardFormGroup.getRawValue()
        for (const item of this.selectedDates) {
            const card = new TimeCard()
            if (this.isLocked() && this.selectedTimeCardContainerGroup) {
                card.date = item.startOf('day').utc(true).toDate()
                card.fullName = this.selectedTimeCardContainerGroup[0].employeeFullName
                card.employeeId = this.selectedTimeCardContainerGroup[0].employeeId
                card.timesheetProjectName = this.selectedTimeCardContainerGroup[0].timesheetProjectName
                card.timesheetProjectId = this.selectedTimeCardContainerGroup[0].timesheetProjectId
                card.clientName = this.selectedTimeCardContainerGroup[0].clientName
            }
            if (this.selectedEmployee && this.selectedProject && this.selectedProject.id && this.selectedEmployee.id) {
                card.date = item.startOf('day').utc(true).toDate()
                card.fullName = this.selectedEmployee.fullName
                card.employeeId = this.selectedEmployee.id
                card.timesheetProjectName = this.selectedProject.name
                card.timesheetProjectId = this.selectedProject.id
                card.clientName = this.selectedProject.clientName
            }
            card.secondsToInvoice = Utils.timeUnitStringToSeconds(formValue.toCharge)
            card.secondsNoInvoice = Utils.timeUnitStringToSeconds(formValue.noCharge)
            card.issueTitle = formValue.issueTitle
            card.worklogComment = formValue.comment
            card.timecardStatusEnum = TimeCardStatus.NEW

            result.push(card)
        }

        return result
    }

    openJira(): void {
        if (this.timeCard && this.timeCard.issueKey) {
            const url = `https://peopleware.atlassian.net/browse/${this.timeCard.issueKey}`
            window.open(url)
        }
    }

    getProjectName(timesheetProject: TimesheetProject): string {
        return timesheetProject.clientName && timesheetProject.name
            ? `${timesheetProject.clientName} - ${timesheetProject.name}`
            : ''
    }

    isReadOrDelete(): boolean {
        return this.data.crud === CrudOps.Read || this.data.crud === CrudOps.Delete
    }

    isReadDeleteOrLocked(): boolean {
        return this.isReadOrDelete() || this.isLocked()
    }

    isLocked(): boolean {
        return this.timeCard?.timecardStatusEnum === TimeCardStatus.LOCKED
    }

    isRead(): boolean {
        return this.data.crud === CrudOps.Read
    }

    private getClientProjectName(timecard: TimeCard): string {
        return timecard.timesheetProjectName && timecard.clientName
            ? `${timecard.clientName} - ${timecard.timesheetProjectName}`
            : ''
    }

    private setSelectedEmployee(employee: Employee): void {
        this.selectedEmployee = employee
    }

    private setSelectedProject(project: TimesheetProject): void {
        this.selectedProject = project
    }

    private getIssuePriority(): string | undefined {
        return this.selectedTimeCardContainerGroup && this.selectedTimeCardContainerGroup[0].issuePriority
            ? this.selectedTimeCardContainerGroup[0].issuePriority.toUpperCase()
            : undefined
    }
}
